import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import { Session } from "@supabase/supabase-js";
import "./Flight.css"

export interface Booking {
    type: string;
    departure_id: string;
    arrival_id: string;
    outbound_date: string;
    return_date: string;
    adults: number;
    stops: number;
    currency: string;
    sort_by: string;
    max_price: number;
    departure_token?: string;
    booking_token?: string;
}

interface FlightProps {
  user: Session['user'];
}

function Flight({ user }: FlightProps) {
  const searchNewFlight = async(booking : Booking) => {
    // need to send to backend to POST to flight search endpoint
    const response = await api.post("/flightsearch", booking);

    console.log("Response:", JSON.stringify(response.data, null, 2));

    if (response.data.hasOwnProperty("error")) {
        throw new Error(response.data["error"]);
    }

    return response.data;
  }

  const [type, setType] = useState("1");  // default set to round trip
  const [departure_id, setDepartureId] = useState("");
  const [arrival_id, setArrivalId] = useState("");
  
  const [outbound_date, setOutboundDate] = useState(new Date().toISOString().split('T')[0]); // default is today
  const [return_date, setReturnDate] = useState(new Date().toISOString().split('T')[0]);

  const [adults, setAdults] = useState(1);
  const [stops, setStops] = useState(0); // default is 0: any number of stops
  const [currency, setCurrency] = useState("USD");
  const [sort_by, setSortBy] = useState("1"); // default is Top Flights
  const [max_price, setMaxPrice] = useState<number>(); // default is unlimited

  const navigate = useNavigate();

  const onInputChangeType = (e: ChangeEvent<HTMLSelectElement>) => {
      setType(e.target.value);
  }
  const onInputChangeDepartureId = (e: ChangeEvent<HTMLInputElement>) => {
      setDepartureId(e.target.value.toUpperCase());
  }
   const onInputChangeArrivalId = (e: ChangeEvent<HTMLInputElement>) => {
      setArrivalId(e.target.value.toUpperCase());
  }
   const onInputChangeOutboundDate = (e: ChangeEvent<HTMLInputElement>) => {
      setOutboundDate(e.target.value);
  }
   const onInputChangeReturnDate = (e: ChangeEvent<HTMLInputElement>) => {
      setReturnDate(e.target.value);
  }
   const onInputChangeAdults = (e: ChangeEvent<HTMLInputElement>) => {
      setAdults(e.target.valueAsNumber);
  }
   const onInputChangeStops = (e: ChangeEvent<HTMLSelectElement>) => {
      setStops(Number(e.target.value));
  }
  const onInputChangeCurrency = (e: ChangeEvent<HTMLInputElement>) => {
      setCurrency(e.target.value.toUpperCase());
  }
  const onInputChangeSortBy = (e: ChangeEvent<HTMLSelectElement>) => {
      setSortBy(e.target.value);
  }
  const onInputChangeMaxPrice = (e: ChangeEvent<HTMLInputElement>) => {
      setMaxPrice(e.target.valueAsNumber);
  }
  
  async function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  const [message, setMessage] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (departure_id.length === 0 || arrival_id.length === 0 || 
    type.length === 0 || currency.length === 0 ||
    sort_by.length === 0) {
        setMessage("Please fill in all fields");
        return;
    }

    if (departure_id.length !== 3 || arrival_id.length !== 3) {
        setMessage("Departure and Arrival IDs must be 3-letter IATA codes");
        return;
    }

    const now = new Date();
    const outbound = new Date(outbound_date);
    const ret = new Date(return_date);

    if (now > outbound || now > ret) {
        setMessage("Outbound and Return dates must be in the future");
        return;
    }

    if (ret < outbound) {
        setMessage("Return date must be after Outbound date");
        return;
    }

    if (adults <= 0) {
        setMessage("Must have at least one traveller");
        return;
    }

    if (max_price !== undefined && Number(max_price) <= 0) {
        setMessage("Max Price must be a positive number");
        return;
    }

    console.log(outbound_date, return_date, adults, stops, currency, sort_by, max_price);

    setMessage("Searching for outbound flight...");

    try {
        const booking: Booking = {
            type,
            departure_id,
            arrival_id,
            outbound_date,
            return_date,
            adults,
            stops,
            currency,
            sort_by,
            max_price: max_price ?? 10000000000
        }

        const response = await searchNewFlight(booking);

        setMessage("Outbound flight found! Showing possible selections...");

        await sleep(2000).then(() => {
          navigate("/outbound-flight-selection", {
            state: {
              booking: booking,
              api_response: response
            }
          });
        });
    } catch (error) {
        console.error("Error searching for flight: ", error);
        setMessage("Unable to find flight, try to change the dates or other parameters.");
    }
  }

  return (
     <>
        <h1>Flight Search</h1>
            <form id="flight-search" onSubmit={handleSubmit}>

              <label htmlFor="type">Trip Type:  </label>
              <select
                required
                value={type}
                id="type"
                name="TripType"
                onChange={onInputChangeType}>

                <option value="">-- Pick an option --</option>
                <option value="1">Round Trip</option>
                <option value="2">One Way</option>
              </select>
              <br />
                <label htmlFor="departure">From Airport (Only IATA codes):  </label>
                <input
                    type="text"
                    value={departure_id}
                    required
                    id="departure"
                    name="From"
                    placeholder="eg. SIN"
                    onChange={onInputChangeDepartureId} />
              <br />
                <label htmlFor="arrival">To Airport (Only IATA codes):  </label>
                <input
                    type="text"
                    value={arrival_id}
                    required
                    id="arrival"
                    name="To"
                    onChange={onInputChangeArrivalId} />
              <br />
                <label htmlFor="outbound_date">Outbound Date:  </label>
                <input
                    type="date"
                    value={outbound_date}
                    required
                    id="outbound_date"
                    name="Outbound Date"
                    onChange={onInputChangeOutboundDate} />
              <br />
                <label htmlFor="return_date">Return Date:  </label>
                <input
                    type="date"
                    value={return_date}
                    required
                    id="return_date"
                    name="Return Date"
                    onChange={onInputChangeReturnDate} />
              <br />
                <label htmlFor="adults">No. of Adults:  </label>
                <input
                    type="number"
                    value={adults}
                    required
                    id="adults"
                    name="No of adults"
                    onChange={onInputChangeAdults} />
              <br />
                <label htmlFor="stops">No. of Stops:  </label>
              <select
                required
                value={stops}
                id="stops"
                name="No of stops"
                onChange={onInputChangeStops}>

                <option value="">-- Pick an option --</option>
                <option value="0">Any number</option>
                <option value="1">Nonstop</option>
                <option value="2">1 Stop or fewer</option>
                <option value="3">2 Stops or fewer</option>
              </select>
              <br />
                <label htmlFor="currency">Currency Abbrev.:  </label>
                <input
                    type="text"
                    value={currency}
                    required
                    id="currency"
                    name="Currency"
                    list="currency-list"
                    onChange={onInputChangeCurrency} />
                <datalist id="currency-list">
                  <option value="USD" />
                  <option value="SGD" />
                  <option value="EUR" />
                  <option value="JPY" />
                  <option value="GBP" />
                  <option value="CNY" />
                </datalist>
              <br />
                <label htmlFor="sort_by">Sort By:  </label>
              <select
                required
                value={sort_by}
                id="sort_by"
                name="Sort By"
                onChange={onInputChangeSortBy}>

                <option value="">-- Pick an option --</option>
                <option value="1">Top flights</option>
                <option value="2">Price</option>
                <option value="3">Departure time</option>
                <option value="4">Arrival time</option>
                <option value="5">Duration</option>
              </select>
              <br />
                <label htmlFor="max_price">Max Price:  </label>
                <input
                    type="number"
                    value={max_price}
                    id="max_price"
                    name="Max Price"
                    placeholder="eg. 3000"
                    onChange={onInputChangeMaxPrice} />
              <br />
              <br />
                <button type="submit">Search Flight</button>
            </form>
            <br />
            <div className="message">
              <strong>
                {message}
              </strong>
            </div>
        </>
  )
}

export default Flight;
